// THE REVIEW STATUS SUB-EVENTS: what a review posted, how its lens slots were sampled,
// and what one pipeline STAGE of it cost.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { RunProvenanceSchema } from '../run-provenance';

/**
 * WHO GitHub showed as the author of what a review lane posted — the fact that decides
 * whether an inline finding or a thread reply carries the visible disclosure footer
 * (owner ruling 2026-09-19; the renderer is `@developerz/review` `renderCommentBody`).
 *
 * - `app` — posted on the claim's GitHub App installation token: the author is `…[bot]`
 *   with GitHub's Bot badge, which IS the disclosure, so the footer is dropped.
 * - `credential` — anything else (no App token resolved): GitHub may show a plain
 *   `User`, so the footer is the only disclosure and it stays.
 *
 * A stored vocabulary (it rides `review_posted` into `task_run_events`), so it lives
 * here rather than in the renderer's package.
 */
export const ReviewPostIdentitySchema = z.enum(['app', 'credential']);
export type ReviewPostIdentity = z.infer<typeof ReviewPostIdentitySchema>;

/**
 * The `review_posted` status event a REVIEW runner streams once it has POSTED a
 * review to GitHub (`apps/runner/src/modes/worker/review.ts` `runReview` — the
 * single `exec.emit('status', { event: 'review_posted', … })` right after the
 * `POST …/reviews` call succeeds). This is the ≥1-billable-action signal for
 * usage metering (#262): the review session ACTED on the PR (posted a review),
 * the exact review analogue of the maintainer loop's "session reached a billable
 * outcome" (`isBillableSession`). A DEFERRED review (a bot detected upstream mints
 * no `pr_review` task at all), a NO-OP pass (a missing spec emits `review_skipped`
 * and returns before posting), and a CRASH before the post all fail to emit this
 * event, so none is ever billed — exactly the "null outcome / no-op / crash is
 * never billable" rule (`@developerz/billing` `metering.ts`).
 *
 * There is no runner→mothership billing transport (a box's creds grant only
 * `fleet.evt.<run_id>`), so — like {@link KbLearningsPayloadSchema} — the audited
 * status event IS the derivation source, and this schema is its trust boundary: a
 * box is untrusted input, and `pr_number` steers a ledger write feeding invoices +
 * the usage dashboard. The mothership resolves the reviewed PR from
 * `(repo, pr_number)` and records ONE idempotent `billable_events` unit keyed on
 * `(repo, item_kind='pr', pull_request_id, billing_window)` — the same dedup key a
 * maintainer session on the PR uses, so a re-review (or a review + a maintainer
 * pass) never double-bills.
 */
/**
 * The most pass kinds a `missing_mandatory` stamp can name, and the longest a kind
 * NAME may be — the wire bounds for {@link ReviewPostedPayloadSchema}'s
 * `missing_mandatory`, exported so `@developerz/review` can pin them against the real
 * `PASS_KIND_LIST` (this package cannot import that enum; the dependency runs the
 * other way). Five kinds exist and the longest is `api_contract` at 12 characters, so
 * 5 is exact and 32 is deliberate slack for a renamed or added kind. Read the field's
 * docblock before changing either: past these bounds a stamp reads `unreadable` and
 * REFUSES a merge, so a bound that falls behind the enumeration parks pull requests.
 */
export const MISSING_MANDATORY_MAX = 5;
export const MISSING_MANDATORY_NAME_MAX = 32;

export const ReviewPostedPayloadSchema = z.object({
  event: z.literal('review_posted'),
  /** GitHub PR number the review posted on — resolved to the billed `pull_requests.id`. */
  pr_number: z.number().int().positive(),
  /**
   * ISO-8601 instant the box POSTED the review — the FIXED billing-window anchor,
   * mirroring the maintainer path's `BillableRecord.handledAt`. Captured on the box
   * at the moment it acted so a redelivery (a crash between persist and ack) can
   * never re-derive the window from a later wall-clock and shift the dedup key
   * across a month boundary — the metering double-bill `metering.ts` warns of.
   * Optional: a box predating the stamp is billed on the ingest clock (a negligible
   * month-boundary residual, never on the hot path).
   */
  at: z.string().datetime().optional(),
  /** Informational (persisted + surfaced, not acted on): the posted verdict + counts. */
  verdict: z.string().max(100).optional(),
  review_event: z.string().max(100).optional(),
  comments: z.number().int().nonnegative().optional(),
  review_id: z.number().int().nullable().optional(),
  /**
   * Present when NO MODEL read the diff (#1478's `ReviewBrainGap`, stamped by
   * the box beside the verdict it downgraded) — the fact the presence gate
   * needs (#1972): a floor review's `comment` is a verdict that read nothing,
   * and `platformVerdictSatisfiesPresence` refuses it as the satisfying review
   * on an app-authored PR. Optional twice over: a model review omits it, and an
   * event from a binary predating the stamp omits it too — absent reads as "no
   * gap", the fail-open direction that converges with the fleet roll.
   */
  brain_gap: z.string().max(100).optional(),
  /**
   * WHO the review posted as ({@link ReviewPostIdentitySchema}) — so "did this review's
   * inline comments carry the visible footer" is answerable from the audit alone.
   * Optional: a binary predating the stamp omits it; absent reads as "not recorded",
   * never as either identity.
   */
  identity: ReviewPostIdentitySchema.optional(),
  /**
   * Run economics stamped beside the verdict (#3158): wall clock, model and
   * provider, model calls, output tokens, the generation halves the sampled
   * slots carried, the slowest single dial, and the observed generation rate.
   * The sticky status comment renders the same numbers. Optional: a binary
   * predating the stamp omits it; absent reads as "not measured".
   */
  economics: z
    .object({
      wall_ms: z.number().int().nonnegative(),
      provider: z.string().max(100),
      model: z.string().max(200),
      calls: z.number().int().nonnegative(),
      output_tokens: z.number().int().nonnegative(),
      generation_ms: z.number().int().nonnegative(),
      generation_output_tokens: z.number().int().nonnegative(),
      slowest_call_ms: z.number().int().nonnegative(),
      output_tokens_per_s: z.number().int().nonnegative().optional(),
    })
    .optional(),
  /**
   * Present when the N-sample ensemble ran SHORT (#2083's `ReviewSamplingGap`,
   * stamped by the box beside the verdict it downgraded) — the aggregate
   * `{requested, completed}` counts across every reviewer this run dispatched.
   * `verdictWithSamplingGap` refuses the recorded verdict as a real APPROVE
   * when this is present, and the merge-gate audit chain has to carry that
   * fact directly rather than reading it out of the summary's prose. Optional:
   * a healthy ensemble omits it, and an event from a binary predating the
   * stamp omits it too — absent reads as "no gap", fail-open.
   */
  sampling_gap: z
    .object({
      requested: z.number().int().nonnegative(),
      completed: z.number().int().nonnegative(),
    })
    .optional(),
  /**
   * The MANDATORY review passes this run did not complete (#4599) — the pass
   * KINDS `missingMandatoryPasses` (`@developerz/review` `passes.ts`) returned,
   * stamped beside the verdict `verdictWithPassMatrix` downgraded for exactly
   * this reason. A model-backed review whose `security` reviewer FAILED records
   * no `brain_gap` (a model ran) and no `sampling_gap` (the samples are
   * complete), so before this stamp the merge gate had no field that could see
   * it and merged on a review that had refused to approve itself.
   *
   * Stamped on EVERY review that computed a matrix, the healthy ones as an EMPTY
   * array — so the value is read by its CONTENT, never by its presence, and a
   * `[]` satisfies presence exactly as a review with nothing missing should.
   * Optional only for the third neighbour's reason: an event from a binary
   * predating the stamp omits it, and ABSENT reads as "not recorded" rather than
   * "a pass was missing" — the same fail-open direction `brain_gap` and
   * `sampling_gap` document above, so no row written before this ships starts
   * parking a pull request.
   *
   * ABSENT IS THE ONLY FAIL-OPEN VALUE. A stamp that is PRESENT and does not match
   * these bounds is a third state, and the merge gate refuses it
   * (`PlatformReviewPass.missingMandatory`, `{ kind: 'unreadable' }`): a box is
   * untrusted input on this path and this row steers a merge, so a record we cannot
   * parse is a review we cannot vouch for. The bounds therefore live HERE, on the
   * wire contract, and not only in the reader that enforces them.
   *
   * THE BOUNDS CITE THEIR ENUMERATION. `missingMandatoryPasses` returns one entry per
   * MANDATORY kind not completed, and its mandatory set is
   * `mandatoryPassesForDiff`'s return — `PASS_KIND_LIST.filter(…)`, deduplicated and
   * kind-ordered, so a repo listing `mandatory_passes: [security, security, …]` in its
   * `.maintainer.yml` still yields each kind at most once. Five kinds exist
   * (`ReviewPassKind`: correctness, security, tests, api_contract, style_nits), so
   * {@link MISSING_MANDATORY_MAX} entries is the STRUCTURAL maximum, not a guess, and
   * the longest name is `api_contract` at 12 characters.
   *
   * A SIXTH KIND MUST RAISE THIS BOUND IN THE SAME PR. `ReviewPassMatrix` is a total
   * type, so TypeScript forces most of that change — but a numeric bound is not a
   * type, and leaving it at five would make every stamp naming six kinds read
   * `unreadable` and park every pull request. `passes.test.ts` pins the bound against
   * `PASS_KIND_LIST` so the omission goes red in CI instead of in production.
   */
  missing_mandatory: z
    .array(z.string().min(1).max(MISSING_MANDATORY_NAME_MAX))
    .max(MISSING_MANDATORY_MAX)
    .optional(),
});
export type ReviewPostedPayload = z.infer<typeof ReviewPostedPayloadSchema>;

/**
 * One per-sample slot a review ensemble actually drove on #2203's fallback
 * path — the per-attempt provider/model the box recovered onto when the
 * primary lane was down. Each entry names the slot's POSITION in the
 * parallel-samples array (`idx`, `0 <= idx`), the provider it drove, and the
 * model id. The provider + model use the SAME closed grammar as
 * {@link RunProvenanceSchema} (alphanumeric segments joined by `. _ - : /`,
 * stripped of markdown/HTML surface so an untrusted box cannot render text
 * into a PR body via this column), because the per-slot stamp lands on the
 * same audit chain `run_provenance` does and the SAME wire fence must close
 * both — re-spelling the grammar in two files is how a producer and a
 * consumer drift into admitting two vocabularies through the same gate.
 */
export const ReviewSampledSlotSchema = z
  .object({
    /** Zero-based index of the slot inside the burst's N parallel samples. */
    idx: z.number().int().nonnegative(),
    /** Wire provider that served the slot, e.g. `anthropic` / `z.ai`. */
    provider: RunProvenanceSchema.shape.provider,
    /** Model id the slot actually drove, e.g. `claude-opus-5`. */
    model: RunProvenanceSchema.shape.model,
    /**
     * #3158: wall clock of the dial that ANSWERED this slot, in milliseconds. A
     * 2-file review once sat 36 minutes in one HTTP call and no row could say
     * which; this is the row. Optional, no default: a box older than #3158 stamps
     * none, and an absent value reads as "unmeasured", never as 0 ms.
     */
    ms: z.number().int().nonnegative().optional(),
    /**
     * #3158: the provider-reported token counts of that dial, present only when
     * the provider reported BOTH on every request the dial made. Absent otherwise
     * — a floor that looks precise is worse than no number. ALL-OR-NOTHING by the
     * refine below (zero is a value; one side alone is a half-measured dial and is
     * refused), so a reader never prices an input count against a missing output.
     */
    input_tokens: z.number().int().nonnegative().optional(),
    output_tokens: z.number().int().nonnegative().optional(),
  })
  .refine((slot) => (slot.input_tokens === undefined) === (slot.output_tokens === undefined), {
    message: 'input_tokens and output_tokens are stamped together or not at all',
    path: ['output_tokens'],
  });
export type ReviewSampledSlot = z.infer<typeof ReviewSampledSlotSchema>;

/**
 * The `review_sampled` status sub-event the box streams on every ensemble
 * burst (apps/runner/src/modes/worker/review/bun-adapter.ts, #1651/#
 * 1684/#2083 — wired by `emitSampling`). Mirrors {@link ReviewPostedPayloadSchema}'s
 * sibling structure: a `z.object` carrying the aggregate per-burst counters
 * the box already emits, PLUS the new optional `slots` array #2203 stamps
 * beside them when the fallback path recovered a sample.
 *
 * Existing aggregate fields are preserved VERBATIM (same names, same types,
 * same optionality) so a producer that today does NOT stamp `slots` parses
 * byte-identically — that byte-identity is the acceptance-criterion check
 * the mothership's PR-body projector and audit re-read rely on. `slots` is
 * the only addition; it is `.optional()` with no `.default()` for the same
 * reason `llm_credential` is — the wire stays lean and existing fixtures /
 * older boxes parse unchanged.
 */
export const ReviewSampledPayloadSchema = z.object({
  event: z.literal('review_sampled'),
  /** Parallel samples the burst asked for (#1651). */
  requested: z.number().int().nonnegative(),
  /** FINAL samples completed across both rounds (the top-up, #2083, feeds in here). */
  completed: z.number().int().nonnegative(),
  /** Findings the samples produced BEFORE the merge. */
  findingsIn: z.number().int().nonnegative(),
  /** Findings the merge collapsed to. */
  merged: z.number().int().nonnegative(),
  /** One entry per failed ATTEMPT, both rounds (#1684: `truncated` / `no_tool_call` / `schema` / `aborted` / `provider`). */
  failures: z.array(z.string().max(100)).optional(),
  /** Samples whose truncation retry fired, whether or not the retry saved them (#1684). */
  retries: z.number().int().nonnegative().optional(),
  /** Slots the bounded second round recovered (#2083). */
  topped_up: z.number().int().nonnegative().optional(),
  /**
   * Per-attempt failure detail (error name + leading message bytes,
   * `SAMPLE_FAILURE_DETAIL_CHARS = 512` per entry, capped at
   * `SAMPLE_FAILURE_DETAIL_MAX = 8` so one payload stays under 4 KB,
   * #2083). The `provider` class alone cannot say whether the burst died
   * to a 429, a 5xx or a timeout, and this bounded string answers that by
   * query.
   */
  failure_details: z.array(z.string().max(512)).optional(),
  /**
   * Per-sample provider/model slot (#2203), plus the dial's wall clock and tokens
   * (#3158), for every slot that ANSWERED. Until #3158 it rode only when the box
   * fell back to the assignment's `llm_credential_fallback` pool, so a
   * primary-only burst parsed byte-identically to #2083's; a box on #3158 stamps
   * it on every burst with at least one answer. Still optional, no default: an
   * all-failed burst carries none, an older box carries none, and an older
   * mothership parses either shape unchanged.
   */
  slots: z.array(ReviewSampledSlotSchema).optional(),
  /**
   * #3158: the longest single dial of the burst in milliseconds, FAILED dials
   * included — `slots[].ms` names only the dials that answered, and the measured
   * defect was a call that never did. Optional for the version-skew reason every
   * field after `merged` is.
   */
  max_ms: z.number().int().nonnegative().optional(),
  /**
   * #3425: slots this burst STOPPED rather than lost — still running when the
   * grace window expired after a peer had already answered, and aborted so they
   * stop costing the account's key.
   *
   * Its own key rather than an entry in `failures`, because it is not one. A
   * grace drop is a deliberate bound with an answer already in hand; a failure
   * is the lane refusing. Collapsing them would make the ONE query that says
   * whether the deadline is working ("how much recall is it costing, and on how
   * many bursts") unanswerable, and would read as an error rate climbing on a
   * box that is simply now bounded. `requested - completed - grace_dropped` is
   * what the lane actually lost.
   *
   * Optional and OMITTED at zero, for the version-skew reason every field after
   * `merged` is: a box with the deadline disabled, and every box older than
   * #3425, emits the payload without it.
   */
  grace_dropped: z.number().int().nonnegative().optional(),
  /**
   * #3425: the same fact for the bounded TOP-UP round (#2083) — dials it stopped
   * rather than lost, once the round's own deadline expired.
   *
   * A SECOND key rather than a wider `grace_dropped`, because they answer
   * different questions: `grace_dropped` says the burst stopped waiting on its
   * PARALLEL samples, this says it stopped waiting on the RETRY of one that
   * failed. The same slot can be counted by both — dropped in round 1 is
   * impossible to top up (#3426 excludes those), but a slot that FAILED in round
   * 1 and was then stopped in round 2 is one slot in two states — so folding them
   * into one number would double-count it and make "how much recall did the
   * deadline cost" unanswerable.
   *
   * Optional and OMITTED at zero, for the version-skew reason every field after
   * `merged` is.
   */
  topup_grace_dropped: z.number().int().nonnegative().optional(),
  /**
   * #2203: how many times the burst DIALLED the sibling lane. `slots` records
   * only the dials that answered, so a fallback success rate derived from it is
   * 100% by construction — this is the denominator.
   */
  fallback_dials: z.number().int().nonnegative().optional(),
  /** #2203: how many of those dials the sibling REFUSED. */
  fallback_failed: z.number().int().nonnegative().optional(),
  /**
   * #2203: whether the sibling lane is demoted for the rest of this REVIEW. The
   * latch is per-run (factory-scoped in `createModelReviewBrain`), so a `true`
   * here on a burst with `fallback_dials: 0` means an EARLIER fan-out group
   * already spent the review's one failed dial.
   */
  fallback_demoted: z.boolean().optional(),
});
export type ReviewSampledPayload = z.infer<typeof ReviewSampledPayloadSchema>;

/**
 * Which pipeline STAGE a review's model call belonged to — the `pass` values 57 audit
 * rows in prod are already keyed on.
 *
 * Closed, and it is the WIRE half of the runner's own `ReviewModelPass`
 * (`apps/runner/src/modes/worker/review-brain/usage-meter.ts`), which carries the
 * quality rationale for each. NOT `ReviewPassKind` (`@developerz/review`), which is a
 * review's LENS — `correctness` / `security` / … — and rides the PLURAL `review_passes`
 * event. A stage answers "which model call"; a kind answers "which concern".
 * `recheck` is the fourth: the review lane re-checking its own open threads at a new head.
 */
export const REVIEW_MODEL_PASSES = ['brain', 'lead', 'verifier', 'recheck'] as const;

/**
 * One review pass's token count. Defaulted to nothing and NOT `.optional()`: the
 * producer reads these off its own meter, so a `review_pass` row without them is a
 * producer that broke rather than a provider that stayed silent.
 */
function passCount(field: string) {
  return z.number().int(`${field}: whole tokens`).nonnegative(`${field}: cannot be negative`);
}

/**
 * The `review_pass` status event (#2550) — what ONE stage of a review cost, on which
 * model, and the QUALITY reason that stage runs on it.
 *
 * WHY IT IS ITS OWN ROW rather than a field on `review_posted`: the three stages are
 * three different jobs against the same diff (extract findings, group files for split
 * reviewers, refute a kept finding), and summing the union would hide what the fan-out
 * lead or the verifier judge spent. The `usage` event this run also publishes is the
 * run's TOTAL through the platform's one token meter; this is the split, and the two are
 * emitted from the same tally so they cannot disagree.
 *
 * `rationale` is prose and is deliberately the ONE loose field: it is a sentence the
 * producer ships in code (#2550 puts the reason next to the choice, not in a doc), it is
 * read by an operator and never rendered into a public PR body, and bounding it is all
 * the shape it needs. Everything else is a closed enum or a whole number.
 *
 * THE CACHE LEGS ARE OPTIONAL AND THAT IS THE CONTRACT (plan 101 slice 01), for the
 * reason `RunUsagePayloadSchema` gives the same two fields: ABSENT means no call in the
 * pass reported a cache count, and `0` means one reported zero. Those are different
 * facts — "this provider tells us nothing about caching" versus "we asked for caching
 * and got no hits" — and the second is the only one a cache-hit rate may be computed
 * from. Writing a defaulted `0` would make the review lane's baseline
 * indistinguishable from a working cache with no hits, which is the measurement the
 * next slice has to move.
 */
export const ReviewPassPayloadSchema = z.object({
  event: z.literal('review_pass'),
  pass: z.enum(REVIEW_MODEL_PASSES),
  /** Model calls the pass made — samples, their retries, every judge. */
  calls: passCount('calls').positive('calls: a pass with no call is not emitted'),
  /** How many of those reported BOTH token counts. `reported < calls` ⇒ a FLOOR. */
  reported: passCount('reported'),
  input_tokens: passCount('input_tokens'),
  output_tokens: passCount('output_tokens'),
  /** Cached input tokens READ. Absent ⇒ no call in the pass reported the leg. */
  cache_read_tokens: passCount('cache_read_tokens').optional(),
  /** Input tokens WRITTEN into the cache. Absent ⇒ no call reported the leg. */
  cache_write_tokens: passCount('cache_write_tokens').optional(),
  /** WHY this pass runs on this model, in quality terms. Never a price. */
  rationale: z.string().min(1).max(500),
  /** The host/provider slug the pass ran on. Absent on a run with no sealed credential. */
  provider: z.string().min(1).max(100).optional(),
  /** The model id the pass ran on. Absent on a run with no sealed credential. */
  model_id: z.string().min(1).max(200).optional(),
});
export type ReviewPassPayload = z.infer<typeof ReviewPassPayloadSchema>;

/** What a strict review may find (#7785 part 2). STORED on `review_strict_posted`: never renamed. */
export const STRICT_REVIEW_FINDING_KINDS = [
  /** The patch runs something at install, build or import time. */
  'install_exec',
  /** The patch reaches the network, or a new host, that the fix does not need. */
  'network',
  /** The patch weakens a test, an assertion, a validation or an auth check. */
  'weakened_check',
  /** What the patch does is not what its description claims. */
  'claim_mismatch',
] as const;
export const StrictReviewFindingKindSchema = z.enum(STRICT_REVIEW_FINDING_KINDS);
export type StrictReviewFindingKind = z.infer<typeof StrictReviewFindingKindSchema>;

/** Bumped when the strict prompt's rules change: a verdict is of the rules it ran under. */
export const STRICT_REVIEW_RULES_VERSION = 1;

/**
 * The `review_strict_posted` status event: the strict review's verdict, a name of its own so no
 * reader of `review_posted` can mistake it for a review, and neither can stand in for the other.
 * Nothing is posted on the PR. A box is untrusted input and `verdict` opens a merge gate, so
 * `findStrictReviewVerdict` reads it only off a task whose ENVELOPE, ours, says strict, and `pass`
 * with a finding reads as a refusal.
 */
export const ReviewStrictPostedPayloadSchema = z.object({
  event: z.literal('review_strict_posted'),
  pr_number: z.number().int().positive(),
  head_sha: z.string().min(7).max(64),
  rules_version: z.number().int().positive(),
  verdict: z.enum(['pass', 'refuse']),
  findings: z
    .array(
      z.object({
        kind: StrictReviewFindingKindSchema,
        path: z.string().max(500).nullable(),
        why: z.string().max(500),
      }),
    )
    .max(50),
});
export type ReviewStrictPostedPayload = z.infer<typeof ReviewStrictPostedPayloadSchema>;
